import { OutputData } from "@editorjs/editorjs";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrianglePersonDigging } from "@fortawesome/sharp-light-svg-icons";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Anchor, AnchorGroup } from "../anchors-navigation";
import FilterButton from "../filtering/Button";
import { ContentSection, HeaderSection, PageViewerProps, StaticContentSection, TableSection } from "./Models";
import BlockContentViewer from "./components/BlockContentViewer";
import BlockHeader from "./components/BlockHeader";
import BlockTableViewer from "./components/BlockTableViewer";
import LibraryLinks from "./components/LibraryLinks";
import StaticContent from "./components/StaticContent";
import { toViewerSection } from "./helpers";

interface AnchorData {
    id: string;
    text: string;
    tag?: string;
}

const ElementPageViewer = ({ page, elements, template, children }: PageViewerProps) => {
    const [content, setContent] = useState<Array<HeaderSection | StaticContentSection | ContentSection | TableSection>>(
        []
    );
    const [anchorData, setAnchors] = useState<AnchorData[]>([]);

    const { t } = useTranslation("common");

    useEffect(() => {
        if (!page) return;

        const anchors: AnchorData[] = [];
        const content: Array<HeaderSection | ContentSection | TableSection> = [];
        for (let i = 0; i < template.sections.length; i++) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const templateSection = template.sections[i] as any;
            // const pageSection = page.sections?.find((x) => x.sectionCode === templateSection.sectionCode);
            // if (!pageSection) continue;

            content.push(toViewerSection(templateSection, page.sections, page.footnotes));

            if (templateSection.sectionType === "page-section-header") {
                let data = { text: "", anchorText: "" };
                try {
                    data = JSON.parse(templateSection.content);
                } catch {
                    data.text = templateSection.content;
                }

                let anchorText = data.text;
                if (data.anchorText && data.anchorText.length > 0) anchorText = data.anchorText;

                anchors.push({
                    id: `block-${templateSection.sectionCode}`,
                    text: anchorText,
                });
            }
        }

        setAnchors(anchors);
        setContent(content);

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page]);

    return (
        <>
            <AnchorGroup>
                <FilterButton />

                {anchorData.map((anchor) => (
                    <Anchor key={anchor.id} id={anchor.id} small>
                        {anchor.text}
                    </Anchor>
                ))}

                <Anchor id="block-comments" small>
                    {t("comments.anchor", { ns: "common" })}
                </Anchor>
            </AnchorGroup>

            <div className="pb-13 mb-13">
                {page.isCompleted === false ? (
                    <div className="rounded-lg bg-primary-100 p-4">
                        <div className="flex">
                            <div className="shrink-0">
                                <FontAwesomeIcon
                                    icon={faTrianglePersonDigging}
                                    size="xl"
                                    aria-hidden="true"
                                    className="text-primary-800"
                                />
                            </div>
                            <div className="ml-3">
                                <h3 className="text-sm font-medium text-primary-800">
                                    {t("incomplete.title", { ns: "elements" })}
                                </h3>
                                <div className="text-sm text-primary-700">
                                    <p>{t("incomplete.description", { ns: "elements" })}</p>
                                </div>
                            </div>
                        </div>
                    </div>
                ) : (
                    <div />
                )}

                {content.map((section, index) => {
                    switch (section.sectionType) {
                        case "page-section-header":
                            return <BlockHeader key={index} section={section} />;

                        case "page-static-content":
                            return <StaticContent key={index} section={section} />;

                        case "content-editor":
                            if (index === content.length - 1) {
                                return <LibraryLinks key={index} data={section.content as OutputData} />;
                            }

                            return (
                                <BlockContentViewer
                                    key={index}
                                    section={section}
                                    data={section.content}
                                    elements={elements}
                                />
                            );

                        case "ids-platform-table-ifc":
                        case "ids-platform-table-gid-geometry":
                        case "ids-platform-table-gid-documentation":
                        case "ids-platform-table-classification":
                        case "ids-platform-table-gid-information":
                            return (
                                <BlockTableViewer
                                    key={index}
                                    section={section}
                                    data={section.content}
                                    config={section.config}
                                />
                            );

                        default:
                            throw new Error("Invalid section type detected.");
                    }
                })}

                {children}
            </div>
        </>
    );
};

export default ElementPageViewer;
